import { useEffect, type RefObject } from 'react';

/**
 * useFocusTrap — Trap Tab focus inside a modal container so keyboard users
 * cannot tab out into the background page. Pair with `role="dialog"` +
 * `aria-modal="true"` and an Escape-to-close handler for a complete modal.
 *
 * On mount: focus the first focusable child (or the container itself if
 * none qualify). On Tab / Shift+Tab: cycle within focusables. On unmount:
 * restore focus to whatever element was focused before mount — so a user
 * who opened the modal from a "+" button lands back on that button.
 *
 * Skips focusables inside `aria-hidden="true"` subtrees and respects
 * `disabled` controls. Listens at the `document` level so React
 * `createPortal`-mounted overlays (which render into `document.body`) are
 * still covered.
 *
 * Pure DOM API — no external focus-trap library required.
 */
export function useFocusTrap(
  isActive: boolean,
  containerRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!isActive) return;
    const root = containerRef.current;
    if (!root) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusables = (): HTMLElement[] => {
      const selector =
        'button:not([disabled]):not([aria-hidden="true"]),' +
        '[href]:not([aria-hidden="true"]),' +
        'input:not([disabled]):not([aria-hidden="true"]),' +
        'select:not([disabled]):not([aria-hidden="true"]),' +
        'textarea:not([disabled]):not([aria-hidden="true"]),' +
        '[tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])';
      return Array.from(
        root.querySelectorAll<HTMLElement>(selector),
      ).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    };

    // Initial focus: first focusable, fall back to container.
    const items = getFocusables();
    if (items.length > 0) {
      items[0].focus();
    } else {
      if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
      root.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusables();
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? focusables.indexOf(active) : -1;
      const last = focusables.length - 1;
      if (e.shiftKey) {
        // Shift+Tab on first item → wrap to last.
        if (idx <= 0) {
          e.preventDefault();
          focusables[last].focus();
        }
      } else {
        // Tab on last item (or no focus inside) → wrap to first.
        if (idx === last || idx === -1) {
          e.preventDefault();
          focusables[0].focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Restore focus to whatever was focused before the modal opened.
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isActive, containerRef]);
}
